import { DELIVERY_ACTION_EVENT } from '@/_features/deliveries/componentes/deliveryman-route-box/constants';
import type { DeliveryRouteData } from '@/_features/deliveries/componentes/deliveryman-route-box/types';
import type { IEvent } from '@/Interfaces/Data';
import { EMonitorRouteStepType } from './constants';
import type {
  GetMonitorDeliveryDetailResponse,
  MonitorRouteStep,
} from './types';

type StepEvents = {
  byDeliveryman: DELIVERY_ACTION_EVENT;
  byManager: DELIVERY_ACTION_EVENT;
};

const STEP_EVENTS: Partial<Record<EMonitorRouteStepType, StepEvents>> = {
  [EMonitorRouteStepType.ACCEPTED]: {
    byDeliveryman:
      DELIVERY_ACTION_EVENT.ENTREGA_ASSOCIADA_ACEITA_BY_DELIVERYMAN,
    byManager: DELIVERY_ACTION_EVENT.ENTREGA_ASSOCIADA_ACEITA_BY_MANAGER,
  },
  [EMonitorRouteStepType.ARRIVED_STORE]: {
    byDeliveryman: DELIVERY_ACTION_EVENT.ENTREGA_CHEGOU_NA_LOJA,
    byManager: DELIVERY_ACTION_EVENT.ENTREGA_CHEGOU_NA_LOJA_BY_MANAGER,
  },
  [EMonitorRouteStepType.LEFT_STORE]: {
    byDeliveryman: DELIVERY_ACTION_EVENT.ENTREGA_SAIU_DA_LOJA_BY_DELIVERYMAN,
    byManager: DELIVERY_ACTION_EVENT.ENTREGA_SAIU_DA_LOJA_BY_MANAGER,
  },
  [EMonitorRouteStepType.ARRIVED_CLIENT]: {
    byDeliveryman:
      DELIVERY_ACTION_EVENT.ENTREGA_CHEGOU_NO_CLIENTE_BY_DELIVERYMAN,
    byManager: DELIVERY_ACTION_EVENT.ENTREGA_CHEGOU_NO_CLIENTE_BY_MANAGER,
  },
  [EMonitorRouteStepType.DELIVERED]: {
    byDeliveryman: DELIVERY_ACTION_EVENT.ENTREGA_ENTREGUE,
    byManager: DELIVERY_ACTION_EVENT.ENTREGA_ENTREGUE_BY_MANAGER,
  },
  [EMonitorRouteStepType.RETURNED_STORE]: {
    byDeliveryman: DELIVERY_ACTION_EVENT.CORRIDA_RETORNOU_LOJA,
    byManager: DELIVERY_ACTION_EVENT.CORRIDA_RETORNOU_LOJA,
  },
  [EMonitorRouteStepType.FINISHED]: {
    byDeliveryman: DELIVERY_ACTION_EVENT.ENTREGA_FINALIZADA_BY_DELIVERYMAN,
    byManager: DELIVERY_ACTION_EVENT.ENTREGA_FINALIZADA_BY_MANAGER,
  },
};

const toRouteEvent = (step: MonitorRouteStep): IEvent | null => {
  const events = STEP_EVENTS[step.type];

  if (!events) {
    return null;
  }

  return {
    _id: `${step.type}-${step.at}`,
    createdAt: step.at,
    updatedAt: step.at,
    evento: step.executedByManager ? events.byManager : events.byDeliveryman,
    payload: step.location ? { lastLocalizacao: step.location } : {},
  };
};

export const toDeliveryRouteData = (
  detail: GetMonitorDeliveryDetailResponse,
): DeliveryRouteData => {
  const { steps, encodedRoute } = detail.route;
  const findStep = (type: EMonitorRouteStepType) =>
    steps.filter(step => step.type === type).at(-1);

  const accepted = findStep(EMonitorRouteStepType.ACCEPTED);
  const arrivedStore = findStep(EMonitorRouteStepType.ARRIVED_STORE);
  const leftStore = findStep(EMonitorRouteStepType.LEFT_STORE);
  const arrivedClient = findStep(EMonitorRouteStepType.ARRIVED_CLIENT);
  const delivered = findStep(EMonitorRouteStepType.DELIVERED);
  const returnedStore = findStep(EMonitorRouteStepType.RETURNED_STORE);
  const finished = findStep(EMonitorRouteStepType.FINISHED);

  return {
    _id: detail._id,
    status: detail.status,
    createdAt: detail.createdAt,
    nomeEstabelecimento: detail.place.name,
    nomeDestinatario: detail.client.name,
    nomeEntregador: detail.deliveryman?.name ?? '',
    localizacaoOrigem: detail.place.address.location,
    localizacaoDestino: detail.client.address.location,
    encodedRoute: encodedRoute ?? undefined,
    deliverymanCurrentLocation: detail.deliveryman?.location ?? undefined,
    eventos: steps
      .map(toRouteEvent)
      .filter((event): event is IEvent => !!event),
    aceiteAt: accepted?.at,
    chegouLojaAt: arrivedStore?.at,
    saiuLojaAt: leftStore?.at,
    chegouClienteAt: arrivedClient?.at,
    entregueAt: delivered?.at,
    retornoLojaAt: returnedStore?.at,
    finalizadoAt: finished?.at,
  };
};
